/**
 * Servicio para generar y gestionar tareas personalizadas por estudiante y lección
 * Genera tareas únicas usando LLM que permiten desarrollar los objetivos asignados
 * Las tareas se almacenan en la tabla user_lesson_progress
 */

import { academicService, LessonObjective, UserLessonProgress } from './academicService';
import llmCredentialsService from './llmCredentialsService';

export interface TaskGenerationContext {
  userEmail: string;
  level: string;
  unit: number;
  lesson: number;
  objectives: LessonObjective[];
  userAge?: number;
  userCity?: string;
  additionalVariables?: Record<string, any>;
}

class PersonalizedTaskService {
  /**
   * Obtiene o genera una tarea personalizada para un estudiante y lección
   * Si ya existe en user_lesson_progress, la retorna. Si no, genera una nueva usando LLM.
   */
  async getOrGenerateTask(
    userEmail: string,
    level: string,
    unit: number,
    lesson: number,
    userAge?: number,
    userCity?: string,
    additionalVariables?: Record<string, any>
  ): Promise<string | null> {
    // Primero intentar obtener tarea existente desde user_lesson_progress
    const progress = await academicService.getUserLessonProgress(userEmail, level, unit, lesson);
    if (progress?.personalized_task) {
      return progress.personalized_task;
    }

    // Si no existe, generar una nueva
    return this.generateTask(userEmail, level, unit, lesson, userAge, userCity, additionalVariables);
  }

  /**
   * Obtiene una tarea personalizada existente desde user_lesson_progress
   */
  async getTask(
    userEmail: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<string | null> {
    try {
      const progress = await academicService.getUserLessonProgress(userEmail, level, unit, lesson);
      return progress?.personalized_task || null;
    } catch (error) {
      console.error('[PersonalizedTaskService] Error al obtener tarea:', error);
      return null;
    }
  }

  /**
   * Genera una nueva tarea personalizada usando LLM y la guarda en user_lesson_progress
   */
  async generateTask(
    userEmail: string,
    level: string,
    unit: number,
    lesson: number,
    userAge?: number,
    userCity?: string,
    additionalVariables?: Record<string, any>
  ): Promise<string | null> {
    // Obtener objetivos de la lección
    const objectives = await academicService.getLessonObjectives(level, unit, lesson);
    
    if (objectives.length === 0) {
      throw new Error('No hay objetivos disponibles para esta lección');
    }

    // Obtener credenciales de OpenAI
    let credentials = llmCredentialsService.getCredentials();
    if (!credentials || !credentials.openaiKey) {
      credentials = await llmCredentialsService.loginAndGetCredentials();
    }

    if (!credentials.openaiKey) {
      throw new Error('No se pudo obtener la API key de OpenAI');
    }

    // Construir contexto para la generación
    const context: TaskGenerationContext = {
      userEmail,
      level,
      unit,
      lesson,
      objectives,
      userAge,
      userCity,
      additionalVariables,
    };

    // Generar tarea usando LLM
    const taskDescription = await this.generateTaskWithLLM(context, credentials.openaiKey);

    // Guardar en user_lesson_progress (actualizar el registro existente o crear uno nuevo)
    const progress = await academicService.getUserLessonProgress(userEmail, level, unit, lesson);
    
    // Determinar la versión de la tarea (incrementar si ya existe, empezar en 1 si es nueva)
    const currentVersion = progress?.task_version || 0;
    const newVersion = currentVersion + 1;
    
    if (progress) {
      // Actualizar registro existente con nueva tarea y versión incrementada
      await academicService.updateUserLessonProgress(userEmail, level, unit, lesson, {
        personalized_task: taskDescription,
        task_version: newVersion
      });
    } else {
      // Si no existe progreso, crear uno nuevo con la tarea (versión 1)
      const initialStatus: Record<string, any> = {};
      objectives.forEach(obj => {
        initialStatus[obj.description] = {
          completed: false,
          correct_count: 0,
          total_correct: 0,
          used_phrases: [],
        };
      });

      await academicService.createUserLessonProgress(userEmail, level, unit, lesson, {
        objectives_status: initialStatus,
        personalized_task: taskDescription,
        task_version: 1
      });
    }

    return taskDescription;
  }

  /**
   * Genera la descripción de la tarea usando LLM
   */
  private async generateTaskWithLLM(
    context: TaskGenerationContext,
    openaiKey: string
  ): Promise<string> {
    const objectivesList = context.objectives
      .map((obj, index) => `${index + 1}. ${obj.description}`)
      .join('\n');

    // Construir información de personalización
    const personalizationInfo: string[] = [];
    if (context.userAge) {
      personalizationInfo.push(`- Edad del estudiante: ${context.userAge} años`);
    }
    if (context.userCity) {
      personalizationInfo.push(`- Ciudad del estudiante: ${context.userCity}`);
    }
    if (context.additionalVariables && Object.keys(context.additionalVariables).length > 0) {
      personalizationInfo.push(`- Variables adicionales: ${JSON.stringify(context.additionalVariables)}`);
    }

    const personalizationText = personalizationInfo.length > 0
      ? `\n\nINFORMACIÓN DE PERSONALIZACIÓN:\n${personalizationInfo.join('\n')}`
      : '';

    const systemPrompt = `Eres un experto en diseño de tareas de aprendizaje para francés como lengua extranjera.

Tu tarea es crear una tarea personalizada y única que permita al estudiante desarrollar los objetivos de aprendizaje de la lección.

REGLAS IMPORTANTES:
1. La tarea debe ser una situación comunicativa realista y atractiva
2. Debe permitir al estudiante practicar TODOS los objetivos de la lección de manera natural
3. Debe ser personalizada y única para cada estudiante (no usar plantillas genéricas)
4. Debe ser apropiada para el nivel del estudiante (${context.level})
5. Si hay información de personalización (edad, ciudad, etc.), úsala para hacer la tarea más relevante
6. La tarea debe ser clara, específica y motivadora
7. Debe describir un escenario o situación que el estudiante pueda imaginar y en el que pueda participar
8. La tarea debe ser ejecutable durante una conversación con un asistente de IA
9. NUNCA menciones estructuras gramaticales específicas (como "usando...", "utilizando...", "con el verbo...", "empleando...")
10. NUNCA menciones nombres de tiempos verbales, formas gramaticales o estructuras lingüísticas
11. La tarea debe describir QUÉ HACER comunicativamente, NO CÓMO hacerlo gramaticalmente
12. El estudiante debe demostrar su conocimiento sin recibir pistas sobre las estructuras que debe usar

FORMATO DE RESPUESTA:
- Responde ÚNICAMENTE con la descripción de la tarea (sin títulos, sin numeración, sin formato adicional)
- La descripción debe ser clara y directa, describiendo la situación o escenario
- Debe ser de 2-4 oraciones, suficiente para que el estudiante entienda qué debe hacer
- NO incluyas instrucciones paso a paso, solo describe la situación/tarea
- NO menciones explícitamente estructuras gramaticales o verbos específicos

EJEMPLOS DE BUENAS TAREAS:
- "Eres un estudiante nuevo en una escuela en París. Tu primera clase es hoy y necesitas presentarte a tu profesor y a tus compañeros. Debes saludar formalmente, decir tu nombre completo, y presentar a un compañero que acabas de conocer."
- "Estás en un restaurante en Lyon y el mesero te pregunta sobre tus preferencias. Debes ordenar comida, describir platos que te gustan, y hacer preguntas sobre el menú."
- "Acabas de llegar a una ciudad nueva y necesitas pedir direcciones. Debes saludar a las personas, preguntar cómo llegar a lugares importantes, y agradecer la ayuda."

EJEMPLOS DE MALAS TAREAS:
- "Practica los objetivos de la lección" (demasiado genérico)
- "Completa los ejercicios de gramática" (no es una situación comunicativa)
- "Repite las frases después del profesor" (no es una tarea personalizada)
- "Presenta a alguien usando el verbo s'appeler" (menciona estructuras gramaticales - PROHIBIDO)
- "Describe tu rutina utilizando verbos pronominales" (menciona estructuras gramaticales - PROHIBIDO)`;

    const userPrompt = `Crea una tarea personalizada para un estudiante de nivel ${context.level}, unidad ${context.unit}, lección ${context.lesson}.

OBJETIVOS QUE DEBE DESARROLLAR LA TAREA:
${objectivesList}${personalizationText}

Genera una tarea única y personalizada que permita al estudiante desarrollar todos estos objetivos de manera natural durante una conversación.`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: systemPrompt,
            },
            {
              role: 'user',
              content: userPrompt,
            },
          ],
          temperature: 0.7, // Temperatura moderada para creatividad pero consistencia
          max_tokens: 300, // Suficiente para una descripción clara
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No se recibió contenido de OpenAI');
      }

      // Limpiar la respuesta (eliminar posibles títulos o formato adicional)
      const cleanedContent = content.trim().replace(/^(Tarea:|Descripción:|Situación:)\s*/i, '').trim();

      return cleanedContent;
    } catch (error) {
      console.error('[PersonalizedTaskService] Error al generar tarea con LLM:', error);
      throw error;
    }
  }

  /**
   * Regenera una tarea existente (útil si se actualizan variables de personalización)
   */
  async regenerateTask(
    userEmail: string,
    level: string,
    unit: number,
    lesson: number,
    userAge?: number,
    userCity?: string,
    additionalVariables?: Record<string, any>
  ): Promise<string | null> {
    // Generar nueva tarea (sobrescribirá la tarea anterior automáticamente)
    // No es necesario limpiar primero, generateTask ya maneja update/create correctamente
    return this.generateTask(userEmail, level, unit, lesson, userAge, userCity, additionalVariables);
  }
}

export const personalizedTaskService = new PersonalizedTaskService();
export default personalizedTaskService;
